// src/components/layout/Header.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuShoppingBag } from "react-icons/lu";
import { useNavigate, Link } from "react-router-dom";
import { fetchProducts } from "../../redux/features/Product/productThunk";

import logo from "../../img/logo2.jpg";

// تابع کمکی برای خواندن امن نام دسته‌بندی
const getCategoryName = (product) => {
  if (!product) return "بدون دسته";
  if (product.category && typeof product.category === "object") {
    return product.category.name || product.category.title || "بدون دسته";
  }
  if (product.category && typeof product.category === "string") {
    return product.category;
  }
  if (product.category_name) return product.category_name;
  if (product.type) return product.type;
  return "بدون دسته";
};

function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userInfo } = useSelector((state) => state.auth) || {};
  const { products } = useSelector(
    (state) => state.listProduct || { products: [] }
  );

  const [isMenueOpen, SetisMenueOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [activeTab, setActiveTab] = useState("categories");
  const [openCategory, setOpenCategory] = useState(null);
  const [selectedCategoryProducts, setSelectedCategoryProducts] = useState([]);
  const [selectedTypeProducts, setSelectedTypeProducts] = useState([]);
  const [showProducts, setShowProducts] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null); // برای debounce

  useEffect(() => {
    if (!products || products.length === 0) {
      dispatch(fetchProducts());
    }
  }, [dispatch, products]);

  // ساخت منوی داینامیک
  const dynamicMenu = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    const categories = [
      ...new Set(products.map((p) => getCategoryName(p))),
    ].filter((cat) => cat && cat !== "بدون دسته");
    return categories.map((cat) => ({
      name: cat,
      products: products.filter((p) => getCategoryName(p) === cat),
      subCategories: [
        ...new Set(
          products
            .filter((p) => getCategoryName(p) === cat && p.type && p.type !== cat)
            .map((p) => p.type)
        ),
      ].filter(Boolean),
    }));
  }, [products]);

  // مدیریت تعداد اقلام سبد خرید
  useEffect(() => {
    const updateCartCount = () => {
      const savedCart =
        localStorage.getItem("cartItems") || localStorage.getItem("eriss_cart");
      const cartItems = savedCart ? JSON.parse(savedCart) : [];
      const totalCount = cartItems.reduce(
        (sum, item) => sum + (item.qty || item.quantity || 0),
        0
      );
      setCartCount(totalCount);
    };

    updateCartCount();
    const handleStorageChange = () => updateCartCount();
    window.addEventListener("storage", handleStorageChange);
    const handleCartUpdate = () => updateCartCount();
    window.addEventListener("cartUpdated", handleCartUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("cartUpdated", handleCartUpdate);
    };
  }, []);

  // فوکوس خودکار روی اینپوت سرچ موبایل
  useEffect(() => {
    if (activeTab === "search" && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 300);
    }
  }, [activeTab]);

  // ✅ اصلاح: اضافه کردن debounce به handleSearch
  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (query.trim() === "") {
      setSearchResults([]);
      setIsSearching(false);
      setIsLoading(false);
      return;
    }

    setIsSearching(true);
    setIsLoading(true);

    searchTimerRef.current = setTimeout(() => {
      const lowerQuery = query.toLowerCase();
      const results = products.filter((p) => {
        const title = (p.name || p.title || "").toLowerCase();
        const cat = getCategoryName(p).toLowerCase();
        const model = (p.model || "").toLowerCase();
        return (
          title.includes(lowerQuery) ||
          cat.includes(lowerQuery) ||
          model.includes(lowerQuery)
        );
      });
      setSearchResults(results);
      setIsLoading(false);
    }, 350);
  };

  const handleCategoryClick = (categoryName) => {
    const cat = dynamicMenu.find((c) => c.name === categoryName);
    if (cat) {
      setIsLoading(true);
      setSelectedCategoryProducts([]);
      setSelectedTypeProducts([]);
      setShowProducts(true);
      setOpenCategory(openCategory === categoryName ? null : categoryName);
      setIsSearching(false);
      setSearchResults([]);
      setSearchQuery("");

      setTimeout(() => {
        setSelectedCategoryProducts(cat.products);
        setIsLoading(false);
      }, 250);
    }
  };

  const handleTypeClick = (categoryName, type) => {
    setIsLoading(true);
    setSelectedCategoryProducts([]);
    setSelectedTypeProducts([]);
    setShowProducts(true);
    setIsSearching(false);
    setSearchResults([]);
    setSearchQuery("");

    setTimeout(() => {
      const productsOfType = products.filter(
        (p) => getCategoryName(p) === categoryName && p.type === type
      );
      setSelectedTypeProducts(productsOfType);
      setIsLoading(false);
    }, 250);
  };

  const closeMenu = () => {
    SetisMenueOpen(false);
    setShowProducts(false);
    setSelectedCategoryProducts([]);
    setSelectedTypeProducts([]);
    setIsSearching(false);
    setSearchResults([]);
    setSearchQuery("");
    setIsLoading(false);
  };

  const openSearchTab = () => {
    SetisMenueOpen(true);
    setActiveTab("search");
    setIsSearching(true);
    setShowProducts(false);
  };

  const handleProfileClick = () => {
    if (userInfo?.access) {
      navigate("/profile");
    } else {
      navigate("/phone?redirect=/profile");
    }
  };

  const SkeletonCard = () => (
    <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-3 animate-pulse bg-gray-50/50">
      <div className="h-14 w-14 rounded-lg bg-gray-200 shrink-0"></div>
      <div className="flex-1 space-y-2">
        <div className="h-3.5 w-3/4 bg-gray-200 rounded"></div>
        <div className="h-3 w-1/2 bg-gray-200 rounded"></div>
      </div>
    </div>
  );

  const SkeletonDesktopItem = () => (
    <div className="flex items-center gap-3 p-3 border-b border-gray-50 animate-pulse">
      <div className="h-11 w-11 rounded-lg bg-gray-200 shrink-0"></div>
      <div className="flex-1 space-y-2">
        <div className="h-3.5 w-3/4 bg-gray-200 rounded"></div>
        <div className="h-3 w-1/3 bg-gray-200 rounded"></div>
      </div>
    </div>
  );

  return (
    <>
      <div className="w-full h-1.5 bg-[#9EAD8C]"></div>

      <header
        dir="rtl"
        className="sticky top-0 z-[8887] flex justify-between items-center bg-white/90 backdrop-blur-md shadow-sm h-[60px] lg:h-[72px] px-4 lg:px-8 border-b border-gray-100 transition-all"
      >
        {/* ۱. سمت راست: منوی همبرگری + آیکون سرچ (موبایل) */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            onClick={() => SetisMenueOpen(true)}
            className="p-1.5 rounded-lg text-gray-700 hover:bg-gray-100 transition"
            aria-label="منوی اصلی"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          <button
            onClick={openSearchTab}
            className="p-1.5 rounded-lg text-gray-700 hover:bg-gray-100 transition"
            aria-label="جستجو"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
        </div>

        {/* لوگو در مرکز (موبایل) */}
        <Link
          to="/"
          className="lg:hidden absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none"
        >
          <img
            src={logo}
            alt="فروشگاه محصولات چوبی"
            className="h-8 w-auto object-contain"
          />
        </Link>

        {/* لوگو در دسکتاپ */}
        <Link
          to="/"
          className="hidden lg:flex items-center select-none"
        >
          <img
            src={logo}
            alt="فروشگاه محصولات چوبی"
            className="h-10 w-auto object-contain"
          />
        </Link>

        {/* ۲. وسط: کادر جستجوی عریض دسکتاپ */}
        <div className="hidden lg:flex flex-1 justify-center max-w-[580px] mx-8">
          <div className="relative w-full">
            <input
              type="text"
              placeholder="جستجوی محصول مورد نظر..."
              className="w-full h-[42px] bg-gray-50/90 border border-[#9EAD8C]/30 rounded-xl pr-4 pl-12 text-[13px] outline-none transition-all focus:bg-white focus:border-[#9EAD8C] focus:ring-2 focus:ring-[#9EAD8C]/20 placeholder:text-gray-400"
              onChange={(e) => handleSearch(e.target.value)}
              value={searchQuery}
            />
            <button
              type="button"
              className="absolute left-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-lg bg-[#9EAD8C] hover:bg-[#8A9A7B] text-white transition shadow-sm"
              aria-label="جستجو"
              onClick={() => {
                if (searchQuery.trim()) {
                  navigate(`/discount?search=${encodeURIComponent(searchQuery)}`);
                  setIsSearching(false);
                }
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>

            {isSearching && searchQuery.trim() !== "" && (
              <div className="absolute top-[48px] right-0 w-full bg-white rounded-2xl shadow-2xl border border-[#9EAD8C]/20 max-h-[380px] overflow-y-auto z-[10000] p-1.5 animate-in fade-in slide-in-from-top-2 duration-150">
                {isLoading ? (
                  <>
                    <SkeletonDesktopItem />
                    <SkeletonDesktopItem />
                    <SkeletonDesktopItem />
                  </>
                ) : searchResults.length > 0 ? (
                  <>
                    {searchResults.slice(0, 7).map((product) => (
                      <div
                        key={product._id || product.id}
                        onClick={() => {
                          navigate(`/product/${product._id || product.id}`);
                          setSearchQuery("");
                          setSearchResults([]);
                          setIsSearching(false);
                        }}
                        className="flex items-center gap-3 p-2.5 hover:bg-gray-50/80 rounded-xl cursor-pointer border-b border-gray-50 last:border-0 transition"
                      >
                        <img
                          src={
                            product.image ||
                            product.images?.[0] ||
                            "https://via.placeholder.com/48"
                          }
                          alt={product.name || product.title}
                          className="h-11 w-11 rounded-lg object-cover bg-gray-50 border border-gray-100 shrink-0"
                        />
                        <div className="flex-1 min-w-0 text-right">
                          <div className="text-[13px] font-medium text-gray-800 truncate">
                            {product.name || product.title}
                          </div>
                          <div className="text-[11px] font-bold text-[#9EAD8C] mt-0.5">
                            {Number(product.price || 0).toLocaleString("fa-IR")} تومان
                          </div>
                        </div>
                      </div>
                    ))}
                    {searchResults.length > 7 && (
                      <div
                        onClick={() => {
                          navigate(`/discount?search=${encodeURIComponent(searchQuery)}`);
                          setIsSearching(false);
                        }}
                        className="p-2 text-center text-[11px] font-bold text-[#9EAD8C] hover:underline cursor-pointer"
                      >
                        مشاهده همه {searchResults.length} نتیجه ←
                      </div>
                    )}
                  </>
                ) : (
                  <div className="p-5 text-center text-gray-400 text-[13px]">
                    محصولی با این مشخصات یافت نشد
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ۳. سمت چپ: آیکون‌های عملیاتی */}
        <div className="flex items-center gap-1 sm:gap-2 text-gray-700">
          <button
            onClick={handleProfileClick}
            className="p-2 rounded-xl hover:bg-gray-100 transition"
            title="حساب کاربری"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M20 21a8 8 0 0 0-16 0" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </button>

          {userInfo?.isAdmin && (
            <button
              onClick={() => navigate("/admin")}
              className="hidden sm:inline-flex items-center text-xs font-bold text-[#8A9A7B] bg-[#9EAD8C]/10 hover:bg-[#9EAD8C]/20 border border-[#9EAD8C]/30 px-2.5 py-1.5 rounded-lg transition"
            >
              پنل مدیریت
            </button>
          )}

          <button
            className="p-2 rounded-xl hover:bg-gray-100 transition hidden sm:block"
            title="علاقه‌مندی‌ها"
            onClick={() => navigate("/favorites")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
            </svg>
          </button>

          <button
            onClick={() => navigate("/cart")}
            className="relative p-2 rounded-xl hover:bg-gray-100 transition"
            title="سبد خرید"
          >
            <LuShoppingBag className="w-5 h-5 text-gray-700" />
            {cartCount > 0 && (
              <span className="absolute top-0.5 right-0.5 bg-[#8A9A7B] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center shadow-sm">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* --- منوی ریسپانسیو سایدبار در موبایل --- */}
      {isMenueOpen && (
        <div className="fixed inset-0 z-[9999] lg:hidden" dir="rtl">
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeMenu}
          ></div>
          <div className="fixed right-0 top-0 h-full w-[85%] max-w-[320px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <span className="font-bold text-gray-800 text-[15px]">
                منوی کاربری
              </span>
              <button
                onClick={closeMenu}
                className="p-1 text-gray-400 hover:text-gray-700 text-lg"
              >
                ✕
              </button>
            </div>

            <div className="flex border-b border-gray-100 text-[13px] font-bold text-center bg-gray-50/50">
              <button
                onClick={() => {
                  setActiveTab("categories");
                  setShowProducts(false);
                  setIsSearching(false);
                  setSearchQuery("");
                }}
                className={`w-1/3 py-3.5 border-b-2 transition ${
                  activeTab === "categories"
                    ? "border-[#9EAD8C] text-[#9EAD8C] bg-white"
                    : "border-transparent text-gray-400"
                }`}
              >
                دسته‌بندی
              </button>
              <button
                onClick={() => {
                  setActiveTab("search");
                  setIsSearching(true);
                  setShowProducts(false);
                }}
                className={`w-1/3 py-3.5 border-b-2 transition ${
                  activeTab === "search"
                    ? "border-[#9EAD8C] text-[#9EAD8C] bg-white"
                    : "border-transparent text-gray-400"
                }`}
              >
                جستجو
              </button>
              <button
                onClick={() => {
                  setActiveTab("menu");
                  setShowProducts(false);
                  setIsSearching(false);
                  setSearchQuery("");
                }}
                className={`w-1/3 py-3.5 border-b-2 transition ${
                  activeTab === "menu"
                    ? "border-[#9EAD8C] text-[#9EAD8C] bg-white"
                    : "border-transparent text-gray-400"
                }`}
              >
                لینک‌ها
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {activeTab === "categories" && (
                <div className="p-3 space-y-1">
                  {showProducts ? (
                    <div>
                      <button
                        onClick={() => {
                          setShowProducts(false);
                          setSelectedCategoryProducts([]);
                          setSelectedTypeProducts([]);
                        }}
                        className="mb-3 flex items-center gap-1.5 text-[12px] text-[#8A9A7B] font-bold hover:underline"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="15 18 9 12 15 6" />
                        </svg>
                        بازگشت به دسته‌ها
                      </button>

                      <div className="space-y-2">
                        {isLoading ? (
                          <>
                            <SkeletonCard />
                            <SkeletonCard />
                            <SkeletonCard />
                          </>
                        ) : (
                          (selectedCategoryProducts.length > 0
                            ? selectedCategoryProducts
                            : selectedTypeProducts
                          ).map((product) => (
                            <div
                              key={product._id || product.id}
                              onClick={() => {
                                navigate(`/product/${product._id || product.id}`);
                                closeMenu();
                              }}
                              className="flex items-center gap-3 rounded-xl bg-gray-50/80 p-2.5 cursor-pointer hover:bg-gray-100 transition border border-gray-100"
                            >
                              <img
                                src={
                                  product.image ||
                                  product.images?.[0] ||
                                  "https://via.placeholder.com/56"
                                }
                                alt={product.name || product.title}
                                className="h-12 w-12 rounded-lg object-cover bg-white"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-medium text-gray-800 truncate">
                                  {product.name || product.title}
                                </div>
                                <div className="text-[11px] font-bold text-[#8A9A7B] mt-0.5">
                                  {Number(product.price || 0).toLocaleString(
                                    "fa-IR"
                                  )}{" "}
                                  تومان
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ) : (
                    dynamicMenu.map((cat, i) => (
                      <div
                        key={i}
                        className="border-b border-gray-100 last:border-0"
                      >
                        <div
                          onClick={() => handleCategoryClick(cat.name)}
                          className={`flex justify-between items-center p-3 rounded-xl cursor-pointer transition ${
                            openCategory === cat.name
                              ? "bg-[#9EAD8C]/10 text-[#8A9A7B] font-bold"
                              : "text-gray-700"
                          }`}
                        >
                          <span className="text-[13px] font-medium">
                            {cat.name}
                            <span className="mr-1.5 text-[10px] text-gray-400 font-normal">
                              ({cat.products.length})
                            </span>
                          </span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-4 w-4 transition duration-200 ${
                              openCategory === cat.name ? "rotate-180" : "rotate-90"
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>

                        {openCategory === cat.name &&
                          cat.subCategories.length > 0 && (
                            <div className="mr-3 pr-3 py-1 space-y-1 border-r-2 border-[#9EAD8C]/30">
                              {cat.subCategories.map((sub, idx) => {
                                const subProducts = products.filter(
                                  (p) =>
                                    getCategoryName(p) === cat.name &&
                                    p.type === sub
                                );
                                return (
                                  <button
                                    key={idx}
                                    onClick={() =>
                                      handleTypeClick(cat.name, sub)
                                    }
                                    className="block w-full text-right py-1.5 px-3 text-[12px] text-gray-600 hover:text-[#8A9A7B] rounded-lg transition"
                                  >
                                    {sub} ({subProducts.length})
                                  </button>
                                );
                              })}
                            </div>
                          )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === "search" && (
                <div className="p-3">
                  <div className="relative mb-3">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="جستجوی محصول..."
                      className="w-full h-[40px] border border-gray-200 rounded-xl pr-4 pl-10 text-sm bg-gray-50 outline-none transition focus:border-[#9EAD8C] focus:ring-2 focus:ring-[#9EAD8C]/20"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                    {searchQuery ? (
                      <button
                        onClick={() => {
                          setSearchQuery("");
                          setSearchResults([]);
                          setIsSearching(false);
                          searchInputRef.current?.focus();
                        }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    ) : (
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {searchQuery.trim() !== "" ? (
                    <div className="space-y-2">
                      {isLoading ? (
                        <>
                          <SkeletonCard />
                          <SkeletonCard />
                          <SkeletonCard />
                        </>
                      ) : searchResults.length > 0 ? (
                        searchResults.map((product) => (
                          <div
                            key={product._id || product.id}
                            onClick={() => {
                              navigate(`/product/${product._id || product.id}`);
                              closeMenu();
                            }}
                            className="flex items-center gap-3 rounded-xl border border-gray-100 p-2.5 cursor-pointer hover:bg-gray-50 transition"
                          >
                            <img
                              src={
                                product.image ||
                                product.images?.[0] ||
                                "https://via.placeholder.com/56"
                              }
                              alt={product.name || product.title}
                              className="h-12 w-12 rounded-lg object-cover bg-gray-50 shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-[13px] font-medium text-gray-800 truncate">
                                {product.name || product.title}
                              </div>
                              <div className="text-[11px] font-bold text-[#8A9A7B] mt-0.5">
                                {Number(product.price || 0).toLocaleString(
                                  "fa-IR"
                                )}{" "}
                                تومان
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-gray-400 py-8 text-[13px]">
                          نتیجه‌ای برای جستجوی شما یافت نشد
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-gray-400 py-8 text-[12px]">
                      نام محصول، مدل یا دسته‌بندی را وارد کنید
                    </div>
                  )}
                </div>
              )}

              {activeTab === "menu" && (
                <ul className="space-y-1 p-3">
                  {/* ✅ لینک وبلاگ حذف شد */}
                  {[
                    { name: "فروشگاه", path: "/" },
                    { name: "تخفیفات", path: "/discount" },
                    { name: "درباره ما", path: "/about" },
                    { name: "تماس با ما", path: "/contact" },
                    { name: "سوالات متداول", path: "/faq" },
                  ].map((item, i) => (
                    <li
                      key={i}
                      onClick={() => {
                        navigate(item.path);
                        closeMenu();
                      }}
                      className="p-3 text-[13px] font-medium text-gray-700 hover:bg-gray-50 rounded-xl cursor-pointer transition"
                    >
                      {item.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Header;  